import * as amplitude from "@amplitude/analytics-browser";
import { ArrowFatLeft, ArrowFatRight, Export } from "@phosphor-icons/react";
import clsx from "clsx";
import html2canvas from "html2canvas";
import { motion } from "motion/react";
import { useMemo } from "react";
import extractUsername from "../lib/extractUsername";
import extractValentine from "../lib/extractValentine";
import type { InstagramData } from "../lib/types";
import Tappable from "./tappable";

const PHRASES = [
  "Yah… could have called that one",
  "We all knew…",
  "It was kinda obvious",
  "Don't act so surprised",
  "Shocker…",
  "Well yes",
  "Like… obviously",
  "We all saw this one coming",
  "hoping you at least soft launched",
  "consider this the hard launch",
  "We been knew",
  "I’m sure you watched every Reel they sent",
  "you’re like 2 peas in a pod!",
  "Tell us something we don’t know",
  "The plot twist that wasn’t",
  "You two? Groundbreaking",
  "You’re the Shrek to their Donkey",
  "You’re the Donkey to their Shrek",
  "Basically Olivia and Chappel",
  "Just like spongebob and patrick",
  "As tight as Taylor and Travis",
  "You’re the mario to their luigi",
  "Like the four seasons orlando and that baby",
  "Literally everyone already knew this",
];

export default function Results({
  index,
  data,
  onNext,
  onPrev,
}: {
  index: number;
  data: InstagramData;
  onNext: (userName: string) => void;
  onPrev?: () => void;
}) {
  const { username, valentine, phrase } = useMemo(() => {
    return {
      username: extractUsername(data),
      valentine: extractValentine(data, index),
      phrase: PHRASES[Math.floor(Math.random() * PHRASES.length)],
    };
  }, [data, index]);

  async function handleShareClick() {
    amplitude.track("results_shared");

    // Trick to fix text being shifted by html2canvas.
    // https://github.com/niklasvh/html2canvas/issues/2775#issuecomment-1316356991
    const style = document.createElement("style");
    document.head.appendChild(style);
    const rules = [
      `body > div:last-child img {
        display: inline-block;
      }`,
      `[data-hide-for-download] {
        display: none !important;
      }`,
      `[data-remove-rotation-for-download] {
        rotate: 0deg !important;
      }`,
    ];
    rules.forEach((rule) => {
      style.sheet?.insertRule(rule);
    });

    const element =
      (document.querySelector("[data-container]") as HTMLElement) ??
      document.body;

    const canvas = await html2canvas(element, {
      scale: 2,
    });
    const dataUrl = canvas.toDataURL("image/png");

    const blob = dataURLToBlob(dataUrl);

    style.remove();

    const file = new File([blob], "igvalentine.png", {
      type: "image/png",
    });

    // Check if mobile device supports sharing
    if (
      navigator.canShare &&
      navigator.canShare({ files: [file] }) &&
      /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
    ) {
      try {
        await navigator.share({
          url: "https://igvalentine.com",
          files: [file],
        });
      } catch (e) {
        console.error(e);
      }
    } else {
      // Download on desktop
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "igvalentine.png";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }
  }

  return (
    <div className="p-4 h-full grid grid-rows-[1fr_max-content] gap-y-4">
      <motion.div
        initial={{ opacity: 0, y: 350 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          type: "spring",
          duration: 0.75,
          bounce: 0.15,
          delay: 0.4,
        }}
        className="flex items-center"
      >
        <div className="relative h-max">
          <div className="absolute inset-0 shadow rounded-lg bg-white size-full -rotate-3" />
          <div className="relative inset-0 size-full bg-white shadow-xl">
            <div className="absolute inset-0">
              <img
                src={
                  [
                    "/stripes-pink.png",
                    "/stripes-green.png",
                    "/stripes-blue.png",
                  ][index]
                }
                alt=""
                className="w-full h-full object-cover"
              />
            </div>
            <div className="h-full p-3">
              <div
                className={clsx(
                  ["p-3 relative z-50 bg-white border-4 h-full"],
                  [
                    "border-brand-dark-pink",
                    "border-[#14ae90]",
                    "border-[#19b2e8]",
                  ][index]
                )}
              >
                <p
                  className={clsx(
                    "text-center font-bold",
                    ["text-[#7c0f30]", "text-[#317a6c]", "text-[#257592]"][
                      index
                    ]
                  )}
                >
                  @{username}’s
                  {index === 1 && " 2nd"}
                  {index === 2 && " 3rd"}
                </p>
                <img
                  src="/wordmark-mini.png"
                  alt="Instagram Valentine"
                  className="mt-1 mx-auto w-5/6"
                />
                <p className="text-sm text-center text-[#7c0f30] font-bold">
                  is&hellip;
                </p>
                <p
                  className={clsx(
                    "mt-1 text-xl text-white rounded-xl px-2 py-1 border-4 border-white shadow-sm font-bold max-w-max mx-auto -rotate-2",
                    ["bg-brand-dark-pink", "bg-[#14ae90]", "bg-[#19b2e8]"][
                      index
                    ]
                  )}
                  data-remove-rotation-for-download
                >
                  {valentine.userName}
                </p>
                <p
                  className={clsx(
                    "mt-4 max-w-max mx-auto px-2 py-0.5 font-semibold rotate-1 text-center leading-tight rounded-sm",
                    [
                      "bg-brand-muddy-light-pink text-brand-dark-pink",
                      "bg-[#b1f4e7] text-[#317a6c]",
                      "bg-[#a6e8ff] text-[#257592]",
                    ][index]
                  )}
                  data-remove-rotation-for-download
                >
                  {phrase}
                </p>
                <div className="mt-3 space-y-1">
                  <Row
                    label="Total DMs"
                    value={valentine.totalMessages.toLocaleString()}
                    bgColor="#a7e8ff"
                  />
                  <Row
                    label="Longest streak"
                    value={`${valentine.longestStreakInDays} day${
                      valentine.longestStreakInDays === 1 ? "" : "s"
                    } 🔥`}
                    bgColor="#e9d8fb"
                  />
                  <Row
                    label="They reacted to"
                    value={`${Math.round(
                      valentine.otherPersonReactionPercentage
                    )}% of DMs`}
                    bgColor="#b1f4e7"
                  />
                  <Row
                    label="Most common react"
                    value={valentine.mostCommonReaction ?? "❤️"}
                    forceEmoji
                    bgColor="#f9cfab"
                  />
                  {/* <Row
                    label="Most active hour"
                    value={`${
                      valentine.mostActiveHour === 0
                        ? 12
                        : valentine.mostActiveHour > 12
                        ? valentine.mostActiveHour - 12
                        : valentine.mostActiveHour
                    } ${valentine.mostActiveHour > 12 ? "PM" : "AM"}`}
                    bgColor="#e9d8fb"
                  /> */}
                  <Row
                    label="Starts the convo"
                    value={valentine.conversationStarter}
                    bgColor="#f5ec9a"
                    oneLine
                  />
                </div>
                <div
                  className={clsx(
                    "mt-4 text-sm flex items-center justify-center gap-x-2 font-semibold",
                    [
                      "text-brand-dark-pink",
                      "text-[#14ae90]",
                      "text-[#19b2e8]",
                    ][index]
                  )}
                >
                  <p>IGValentine.com</p>
                  <div
                    className={clsx(
                      "w-[2px] h-4 rounded-lg",
                      ["bg-brand-light-pink", "bg-[#b1f4e7]", "bg-[#a6e8ff]"][
                        index
                      ]
                    )}
                  />
                  <a
                    href="https://locket.camera"
                    target="_blank"
                    className="block"
                    onClick={() =>
                      amplitude.track("locket_clicked_from_results")
                    }
                  >
                    Presented by{" "}
                    <img
                      src={
                        [
                          "/locket-logo-pink.png",
                          "/locket-logo-green.png",
                          "/locket-logo-blue.png",
                        ][index]
                      }
                      alt="Locket"
                      className="inline-block h-4 ml-1 -translate-y-px"
                    />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
        data-hide-for-download
      >
        {index === 0 ? (
          <div className="grid grid-cols-[max-content_1fr] gap-x-4">
            <Tappable>
              <button
                className="h-full bg-brand-brown rounded-2xl text-white flex items-center justify-center gap-x-2 py-2 px-5"
                onClick={handleShareClick}
              >
                <Export size={20} weight="bold" />
                <p className="text-lg font-semibold">Share</p>
              </button>
            </Tappable>
            <Tappable>
              <button
                className="relative w-full flex-1 bg-brand-dark-pink rounded-2xl text-white gap-x-2 py-2 px-5"
                onClick={() => onNext?.(valentine.userName)}
              >
                <p className="text-lg font-semibold leading-tight text-left">
                  See next <br /> Valentine{" "}
                  <ArrowFatRight
                    size={20}
                    weight="fill"
                    className="inline-block"
                  />
                </p>
                <img
                  src="/question-mark-cards-2.png"
                  alt=""
                  className="w-20 absolute right-2 -top-6"
                />
              </button>
            </Tappable>
          </div>
        ) : (
          <div className="grid grid-cols-[max-content_1fr_max-content] gap-x-4">
            <Tappable>
              <button
                className="h-full bg-brand-muddy-light-pink rounded-2xl text-brand-dark-pink py-4 px-6"
                onClick={onPrev}
              >
                <ArrowFatLeft size={20} weight="fill" />
              </button>
            </Tappable>
            <Tappable>
              <button
                className="h-full w-full bg-brand-brown rounded-2xl text-white flex items-center justify-center gap-x-2 py-2 px-5"
                onClick={handleShareClick}
              >
                <Export size={20} weight="bold" />
                <p className="text-lg font-semibold">Share</p>
              </button>
            </Tappable>
            <Tappable>
              <button
                className={clsx(
                  "relative h-full bg-brand-dark-pink rounded-2xl text-white py-4 px-6",
                  index !== 2 && "pr-14"
                )}
                onClick={() => onNext(valentine.userName)}
              >
                <ArrowFatRight size={20} weight="fill" />

                {index !== 2 && (
                  <img
                    src="/question-mark-cards-1.png"
                    alt=""
                    className="w-12 absolute right-0 -top-6"
                  />
                )}
              </button>
            </Tappable>
          </div>
        )}
      </motion.div>
    </div>
  );
}

function Row({
  label,
  value,
  forceEmoji = false,
  bgColor,
  oneLine = false,
}: {
  label: string;
  value: string;
  forceEmoji?: boolean;
  bgColor: string;
  oneLine?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-x-2 group">
      <p className="text-lg font-bold text-brand-dark-pink whitespace-nowrap">
        {label}
      </p>
      <p
        className={`text-lg font-bold text-brand-dark-pink rounded-xl px-2 py-0.5 shadow-sm border-4 border-white group-odd:-rotate-2 group-even:rotate-2 ${
          oneLine && "line-clamp-1"
        }`}
        style={{
          backgroundColor: bgColor,
          fontFamily: forceEmoji ? "Apple Color Emoji, sans-serif" : undefined,
        }}
        data-remove-rotation-for-download
      >
        {value}
      </p>
    </div>
  );
}

function dataURLToBlob(dataURL: string) {
  const parts = dataURL.split(";base64,");
  const contentType = parts[0].split(":")[1];
  const raw = window.atob(parts[1]);
  const rawLength = raw.length;
  const uInt8Array = new Uint8Array(rawLength);

  for (let i = 0; i < rawLength; ++i) {
    uInt8Array[i] = raw.charCodeAt(i);
  }

  return new Blob([uInt8Array], { type: contentType });
}
